import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Pagination } from './ui/pagination';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2,
  Receipt,
  Calendar,
  MoreHorizontal,
  Send,
  X,
  Package,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  invoiceApiService, 
  InvoiceHeader, 
  InvoiceStatus,
  InvoiceHeaderFilters
} from '../services/invoiceApi';
import { formatDate } from '../utils/dateUtils';
import { useBranch } from '../contexts/BranchContext';

const InvoiceListingPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  
  // State
  const [invoices, setInvoices] = useState<InvoiceHeader[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<InvoiceHeader[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  // Filters
  const [filters, setFilters] = useState<InvoiceHeaderFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Load invoices (branchId is mandatory)
  const loadInvoices = useCallback(async () => {
    if (selectedBranch == null) {
      setInvoices([]);
      setFilteredInvoices([]);
      setLoading(false);
      setTotalPages(1);
      return;
    }
    try {
      setLoading(true);
      const requestFilters = { ...filters, branchId: selectedBranch };
      const response = await invoiceApiService.getInvoiceHeaders(currentPage, pageSize, requestFilters);
      // console.log('Invoice API Response:', response); // Debug log
      // console.log('Response success:', response?.success);
      // console.log('Response data:', response?.data);
      // console.log('Response data type:', typeof response?.data);
      // console.log('Response data is array:', Array.isArray(response?.data));
      
      if (response && response.success) {
        const invoiceData = response.data || [];
        // console.log('Setting invoices to:', invoiceData);
        setInvoices(Array.isArray(invoiceData) ? invoiceData : []);
        setFilteredInvoices(Array.isArray(invoiceData) ? invoiceData : []);
        const apiTotalPages = Number((response as any)?.pagination?.totalPages);
        const apiCurrentPage = Number((response as any)?.pagination?.currentPage);
        setTotalPages(!Number.isNaN(apiTotalPages) && apiTotalPages > 0 ? apiTotalPages : 1);
        if (!Number.isNaN(apiCurrentPage) && apiCurrentPage > 0 && apiCurrentPage !== currentPage) {
          setCurrentPage(apiCurrentPage);
        }
      } else {
        // console.warn('Invoice API response not successful:', response);
        setInvoices([]);
        setFilteredInvoices([]);
        setTotalPages(1);
      }
    } catch (error) {
      // console.error('Error loading invoices:', error);
      toast.error('Failed to load invoices');
      setInvoices([]);
      setFilteredInvoices([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters, selectedBranch]);

  // Load invoices on component mount and when filters change
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Apply filters
  const applyFilters = useCallback(() => {
    // console.log('Applying filters. Current invoices:', invoices);
    // console.log('Invoices length:', invoices?.length);
    // console.log('Invoices is array:', Array.isArray(invoices));
    
    // Ensure invoices is an array before filtering
    if (!Array.isArray(invoices)) {
      // console.log('Invoices is not an array, setting filtered to empty');
      setFilteredInvoices([]);
      return;
    }
    
    let filtered = [...invoices];
    // console.log('Starting with filtered invoices:', filtered.length);
    
    // Apply search query
    if (searchQuery) {
      // console.log('Applying search query:', searchQuery);
      filtered = filtered.filter(invoice => 
        invoice.invoiceNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.buyerOrderNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.billToName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      // console.log('After search filter:', filtered.length);
    }
    
    // console.log('Final filtered invoices:', filtered.length);
    setFilteredInvoices(filtered);
  }, [invoices, searchQuery]);

  // Apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Handle status filter change
  const handleStatusFilterChange = (status: string) => {
    setCurrentPage(1);
    setStatusFilter(status);
    setFilters((prev) => ({
      ...prev,
      status: status && status !== 'all' ? (status as any) : undefined,
    }));
  };

  const handleDateFilterChange = (field: 'invoiceDateFrom' | 'invoiceDateTo', value: string) => {
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  // Handle view invoice
  const handleViewInvoice = (id: number) => {
    navigate(`/invoices/${id}/view`);
  };

  // Handle edit invoice
  const handleEditInvoice = (id: number) => {
    navigate(`/invoices/${id}/edit`);
  };


  // Get items count from invoice details
  const getItemsCount = (invoice: InvoiceHeader) => {
    return invoice.invoiceDetails?.length || 0;
  };

  const getManualItemsCount = (invoice: InvoiceHeader) => {
    const details = invoice.invoiceDetails;
    if (!Array.isArray(details)) return 0;
    return details.filter((d) => Boolean(d.isManual ?? d.is_manual)).length;
  };

  // Handle delete invoice
  const handleDeleteInvoice = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await invoiceApiService.deleteInvoiceHeader(id.toString());
        toast.success('Invoice deleted successfully');
        loadInvoices(); // Reload the list
      } catch (error) {
        // console.error('Error deleting invoice:', error);
        toast.error('Failed to delete invoice');
      }
    }
  };


  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Invoice No',
      'Invoice Date',
      'Buyer Order No',
      'Customer',
      'Total Amount',
      'Status',
      'Created Date'
    ];
    
    const csvData = filteredInvoices.map(invoice => [
      invoice.invoiceNo || `INV${invoice.id.toString().padStart(6, '0')}`,
      invoice.invoiceDate || '',
      invoice.buyerOrderNo || '',
      invoice.billToName || '',
      invoice.totalAmount?.toString() || '',
      invoice.status || '',
      invoice.createdAt || ''
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-xl">Invoice Management</CardTitle>
                  <CardDescription className="text-primary-100">
                    Manage and track all invoices
                  </CardDescription>
                </div>
              </div>
              <Button
                onClick={() => navigate('/invoices/new')}
                className="bg-white text-primary-700 hover:bg-primary-50 shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Filters and Search */}
        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="flex items-center gap-2 text-primary-800">
              <FileText className="h-5 w-5 text-primary-700" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div className="space-y-2">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="search"
                    placeholder="Search by invoice no, buyer order no, or customer name..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                  <SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value={InvoiceStatus.DRAFT}>DRAFT</SelectItem>
                    <SelectItem value={InvoiceStatus.E_INVOICED}>E_INVOICED</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoiceDateFrom">Invoice Date From</Label>
                <Input
                  id="invoiceDateFrom"
                  type="date"
                  value={filters.invoiceDateFrom || ''}
                  onChange={(e) => handleDateFilterChange('invoiceDateFrom', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoiceDateTo">Invoice Date To</Label>
                <Input
                  id="invoiceDateTo"
                  type="date"
                  value={filters.invoiceDateTo || ''}
                  onChange={(e) => handleDateFilterChange('invoiceDateTo', e.target.value)}
                />
              </div>
              
              <div className="flex items-end">
                <Button
                  onClick={exportToCSV}
                  variant="outline"
                  className="w-full border-primary-200 text-primary-700 hover:bg-primary-50"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoices Table */}
        <Card className="border-primary-200 shadow-md">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200">
            <CardTitle className="text-lg text-primary-800">Invoice List</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
          {selectedBranch == null ? (
            <div className="text-center py-8 text-gray-500 bg-gradient-to-r from-pink-50 to-pink-100/50 rounded-lg border border-pink-200">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium mb-2">Select a branch</p>
              <p className="text-sm">Please select a branch from the header to view invoices.</p>
            </div>
          ) : loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading invoices...</p>
            </div>
          ) : filteredInvoices.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                    <TableHead className="font-semibold text-gray-700">Invoice No.</TableHead>
                    <TableHead className="font-semibold text-gray-700">Customer</TableHead>
                    <TableHead className="font-semibold text-gray-700 flex items-center gap-1">
                      Invoice Date 
                      <span className="text-gray-400">↓</span>
                    </TableHead>
                    <TableHead className="font-semibold text-gray-700">Amount</TableHead>
                    <TableHead className="font-semibold text-gray-700">Items</TableHead>
                    <TableHead className="font-semibold text-gray-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice, index) => {
                    const itemsCount = getItemsCount(invoice);
                    const manualItemsCount = getManualItemsCount(invoice);
                    return (
                      <TableRow 
                        key={invoice.id} 
                        className="hover:bg-primary-50/50"
                      >
                        <TableCell className="font-medium text-primary-700">
                          {invoice.invoiceNo || `INV${invoice.id.toString().padStart(6, '0')}`}
                        </TableCell>
                        <TableCell className="font-medium text-gray-900">
                          {invoice.billToName || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="h-4 w-4" />
                            {invoice.invoiceDate ? formatDate(invoice.invoiceDate) : 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-bold text-gray-900">
                            ₹{invoice.grandTotal?.toLocaleString() || '0'}
                          </div>
                          <div className="text-xs text-gray-500">
                            Subtotal: ₹{(invoice.totalAmount || 0).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            Tax: ₹{(invoice.grandTotalTax || 0).toLocaleString()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge className="bg-primary-50 text-primary-800 border-primary-200 flex items-center gap-1 w-fit">
                              <Package className="h-3 w-3" />
                              {itemsCount} items
                            </Badge>
                            <div
                              className={
                                manualItemsCount > 0
                                  ? 'text-xs text-orange-600 font-medium'
                                  : 'text-xs text-gray-500'
                              }
                            >
                              Manual: {manualItemsCount}{' '}
                              {manualItemsCount === 1 ? 'entry' : 'entries'}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewInvoice(invoice.id)}
                              className="h-8 w-8 p-0 hover:bg-primary-50"
                              title="View Invoice"
                            >
                              <Eye className="h-4 w-4 text-primary-600" />
                            </Button>
                            {invoice.status !== InvoiceStatus.E_INVOICED && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditInvoice(invoice.id)}
                                  className="h-8 w-8 p-0 hover:bg-emerald-50"
                                  title="Edit Invoice"
                                >
                                  <Edit className="h-4 w-4 text-emerald-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteInvoice(invoice.id)}
                                  className="h-8 w-8 p-0 hover:bg-red-50"
                                  title="Delete Invoice"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200">
              <Receipt className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium mb-2">No Invoices Found</p>
              <p className="text-sm mb-4">Try adjusting your search criteria or create a new invoice.</p>
              <Button
                onClick={() => navigate('/invoices/new')}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
            </div>
          )}
        </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <Card className="border-primary-200 shadow-md">
            <CardContent className="pt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InvoiceListingPage;
